import React from 'react';
import cls from 'classnames';
import noop from 'lodash/noop';

// @ts-ignore
import LoadingDots from '@ott/loading-dots';
import styles from './Button.scss';

import CenteredWithIcon, {
  ICON_ANCHOR,
  ICON_POSITION,
  CenteredWithIconType,
} from './CenteredWithIcon';

export { ICON_ANCHOR, ICON_POSITION };

/** @deprecated use string literal instead */
export enum BUTTON_SIZE {
  DEFAULT = 'defaultSize',
  MEDIUM = 'medium',
  SMALL = 'small',
}

/** @deprecated use string literal instead */
export enum BUTTON_PURPOSE {
  DEFAULT = 'defaultPurpose',
  SECONDARY = 'secondary',
  NEUTRAL = 'neutral',
}

/** @deprecated use string literal instead */
export enum BUTTON_TYPE {
  BUTTON = 'button',
  SUBMIT = 'submit',
  RESET = 'reset',
}

//Todo Some changes for test publish

const mainLocator = 'Button';

type ButtonProps = {
  /**
   * Контент для отображения в кнопке
   */
  children?: React.ReactNode;
  /**
   * React Ref для нативного DOM элемента
   */
  innerRef?: React.RefObject<HTMLButtonElement>;
  /**
   * Тип кнопки
   */
  type?: 'button' | 'submit' | 'reset';
  /**
   * Размер кнопки
   */
  size?: 'defaultSize' | 'medium' | 'small';
  /**
   * Вид кнопки
   */
  purpose?: 'defaultPurpose' | 'secondary' | 'neutral';
  /**
   * Ставит ширину кнопки в 100%
   */
  withFullWidth?: boolean;
  /**
   * Объект для отображения иконки
   */
  iconConfig?: CenteredWithIconType;
  /**
   * Доступность кнопки
   */
  isDisabled?: boolean;
  /**
   * Отображение анимации загрузки в кнопке
   */
  isLoading?: boolean;
  /**
   * Для управления скруглением на уровне родителя, кейс: десктоп - стыкуем кнопку к полю, мобайл - кнопка в отдельной строке
   */
  hasNoBorderRadius?: boolean;
  /**
   * Передаем data-locator для тестирования
   */
  specificLocators?: string | string[];
  /**
   * Передаем data-gtm-locator
   */
  trackingLocators?: string | string[];
  /**
   * Дополнительные стили для контейнера
   */
  childrenClassNames?: { children?: string };
} & React.ButtonHTMLAttributes<HTMLButtonElement> &
  React.AnchorHTMLAttributes<HTMLAnchorElement>;

type NativeButtonProps = Pick<
  ButtonProps,
  'name' | 'className' | 'onClick' | 'onFocus' | 'onMouseDown' | 'tabIndex' | 'style'
> & { 'data-locator'?: string; 'data-gtm-locator'?: string };

const Button: React.FC<ButtonProps> = ({
  children,
  className,
  innerRef,
  type = 'button',
  size = 'defaultSize',
  purpose,
  withFullWidth = false,
  iconConfig,
  isDisabled = false,
  isLoading,
  target,
  form,
  hasNoBorderRadius = false,
  style,
  href,
  rel,
  onClick = noop,
  onFocus = noop,
  onMouseDown = noop,
  specificLocators = [],
  trackingLocators,
  name,
  tabIndex,
  childrenClassNames = {},
}) => {
  if (rel && href && target === '_blank') {
    if (!(/noreferrer/.test(rel) || /noopener/.test(rel))) {
      throw new Error(
        'Forbid target="_blank" attribute without rel="noopener noreferrer". See https://mathiasbynens.github.io/rel-noopener/ for more details '
      );
    }
  }

  const renderChildren = () => {
    // There is no spec for "small" size in the GUI and icon of standard size isn't fit for the case
    if (!iconConfig || size === 'small') {
      return children;
    }

    return (
      <CenteredWithIcon {...iconConfig} className={cls(styles.icon, iconConfig.className)}>
        {children}
      </CenteredWithIcon>
    );
  };

  const renderContent = () => (
    <>
      <div className={cls(styles.children, childrenClassNames.children)}>{renderChildren()}</div>
      {isLoading && (
        <div className={styles.loading}>
          <LoadingDots itemClassName={styles.loadingItem} />
        </div>
      )}
    </>
  );

  const props: NativeButtonProps = {
    name,
    className: cls(
      styles.Button,
      {
        [styles.isSmall]: size === 'small',
        [styles.isMedium]: size === 'medium',
        [styles.withFullWidth]: withFullWidth,
        [styles.isDisabled]: isDisabled,
        [styles.isLoading]: isLoading,
        [styles.hasNoBorderRadius]: hasNoBorderRadius,
        [styles.isSecondary]: purpose === 'secondary',
        [styles.isNeutral]: purpose === 'neutral',
      },
      className
    ),
    onClick,
    onFocus,
    onMouseDown,
    tabIndex,
    style,
    'data-locator': cls(mainLocator, specificLocators),
  };

  if (trackingLocators) {
    props['data-gtm-locator'] = cls(trackingLocators);
  }

  const contentNode = renderContent();

  if (href) {
    return (
      // @ts-ignore
      <a {...props} href={href} target={target} rel={rel}>
        {contentNode}
      </a>
    );
  }

  return (
    // @ts-ignore
    <button {...props} ref={innerRef} form={form} type={type} disabled={isDisabled || isLoading}>
      {contentNode}
    </button>
  );
};

export default React.memo(Button);
export type Props = ButtonProps;
